import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getCollectionsDialerConfig, getLoanTagSuggestions, saveCollectionsDialerConfig } from 'services'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

interface CollectionsDialerConfig {
  enabled: boolean
  callerId: string
  agentRingTimeout: number
  contactRingTimeout: number
  recordCalls: boolean
  recordingAnnouncement: string
  queueDaysPastDue: number
  queueAmountWeight: number
  exceptionTags: string[]
  queueFirstPaymentPoints: number
  queueSecondPaymentPoints: number
  autoResolveCured: boolean
}

/**
 * The selected tags, as the multiselect wants them.
 *
 * A list and not the `{tag: true}` map the other inputs on this screen pass. MultiSelect reads a
 * map through `defaultSelected`, which is on by default and counts a key that is *absent* as
 * selected - so an empty exception list would have displayed, and saved back, as every tag on the
 * book. A list also carries the typed-in tags: `allowCustomValues` is ignored unless the value is
 * one, which is the whole point of letting a servicer name a tag no loan carries yet.
 */
const toTags = (value: any): string[] =>
  (Array.isArray(value) ? value : []).map((tag) => String(tag ?? '').trim()).filter(Boolean)

/** Numbers, grouped by the question they answer rather than by where they are stored. */
const groups: Array<{ title: string; description: string; keys: string[] }> = [
  {
    title: 'The call',
    description:
      'The company number the borrower sees, and how long each phone rings. Yours rings first: the borrower is only dialed once you have picked up, so nobody is left listening to silence.',
    keys: ['callerId', 'agentRingTimeout', 'contactRingTimeout'],
  },
  {
    title: 'Recording',
    description:
      'Whether calls are recorded, and what both parties hear before the borrower is dialed. Whether you may record at all depends on the states you lend in, so this ships off.',
    keys: ['recordingAnnouncement'],
  },
  {
    title: 'The work list',
    description:
      'How a loan gets onto the collections queue and where it lands in the order. Ranking is 10 points a day past due, plus the weight below for each $1,000 of unpaid balance, plus a large push for a broken promise or a callback that has come due.',
    keys: ['queueDaysPastDue', 'queueAmountWeight', 'exceptionTags'],
  },
  {
    title: 'First and second payment defaults',
    description:
      'What a missed first or second payment is worth in the ranking. A loan that never made a payment is not a rung on the delinquency ladder: it says the file may never have performed, which is why these are worked first. At 10 points a day, 1,000 points puts a first payment default above a loan three months down on the morning it is detected. Set either to 0 to rank them like any other past-due account.',
    keys: ['queueFirstPaymentPoints', 'queueSecondPaymentPoints'],
  },
]

const configInputs = (): Record<string, InputType> => ({
  callerId: {
    inputType: 'text',
    type: 'text',
    title: 'Outbound caller ID',
    tooltip:
      'The number the borrower sees, in +15125550100 form. It must be a number your telephony account owns or has verified, or the carrier will refuse the call.',
    required: true,
  },
  agentRingTimeout: {
    inputType: 'text',
    type: 'number',
    title: 'Seconds to ring your phone',
    tooltip: 'Kept short so a missed call rolls up here rather than into your own voicemail.',
    required: true,
  },
  contactRingTimeout: {
    inputType: 'text',
    type: 'number',
    title: 'Seconds to ring the borrower',
    required: true,
  },
  recordingAnnouncement: {
    inputType: 'text',
    type: 'text',
    title: 'What both parties hear first',
    tooltip: 'Played before the borrower is dialed, so the notice comes at the start of the conversation.',
    span: 'full',
    required: false,
  },
  queueDaysPastDue: {
    inputType: 'text',
    type: 'number',
    title: 'Days past due before a loan joins the queue',
    required: true,
  },
  queueAmountWeight: {
    inputType: 'text',
    type: 'number',
    title: 'Ranking points per $1,000 of balance',
    tooltip:
      'Against 10 points for each day past due. Keep it small: how long a borrower has been silent predicts collectability better than how much they owe.',
    required: true,
  },
  exceptionTags: {
    inputType: 'multiselect',
    title: 'Tags that keep a loan off the queue',
    tooltip:
      'A loan carrying any of these is never added, and one already on the list is cleared off with the tag as the reason. The choices are the tags already in use on your loans; type to add one that is not. A loan in bankruptcy is always held back, whether or not it is listed here.',
    options: [],
    allowCustomValues: true,
    customValuePlaceholder: 'Add a tag',
    span: 'full',
    required: false,
    value: [],
  },
  queueFirstPaymentPoints: {
    inputType: 'text',
    type: 'number',
    title: 'Ranking points for a missed first payment',
    tooltip: 'Against 10 points for each day past due, so 1,000 is worth 100 days of ordinary lateness.',
    required: true,
  },
  queueSecondPaymentPoints: {
    inputType: 'text',
    type: 'number',
    title: 'Ranking points for a missed second payment',
    tooltip: 'Kept below the first: the loan did perform once.',
    required: true,
  },
})

/**
 * Calling, and the work list behind it.
 *
 * Two switches here decide whether anything happens at all, and both ship off. Calling itself is
 * off until somebody turns it on, so adding a dialer to the platform cannot start ringing
 * borrowers, and recording is off because whether a servicer may record a borrower is a question
 * of the states they lend in, not one the software can answer for them.
 */
export const CollectionsDialerConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [config, setConfig] = useState<CollectionsDialerConfig | null>(null)
  const [inputs, setInputs] = useState<Record<string, InputType>>(configInputs())
  const [isChanged, setChanged] = useState(false)

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const data = await getCollectionsDialerConfig()
      setConfig(data)
      const temp = configInputs()
      Object.keys(temp).forEach((key) => (temp[key].value = (data as any)?.[key]))
      temp.exceptionTags.value = toTags(data?.exceptionTags)

      // The tags already on the book, so the list is picked rather than typed from memory: a tag
      // spelled differently here holds nothing back, and nothing would say so. Whatever is
      // already configured stays offered even if no loan carries it today, so saving the form
      // again cannot quietly drop it.
      const suggestions = await getLoanTagSuggestions('').catch(() => [])
      ;(temp.exceptionTags as any).options = Array.from(
        new Set([...(suggestions || []), ...(data?.exceptionTags || [])]),
      ).sort((a, b) => a.localeCompare(b))

      setInputs(temp)
    } finally {
      setLoading(false)
    }
  }

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(inputs)
    temp[key].value = InputConvert(temp[key], value)
    temp[key].error = InputValidate(temp[key])
    setInputs(temp)
    setChanged(true)
  }

  const setFlag = (key: 'enabled' | 'recordCalls' | 'autoResolveCured', value: boolean) => {
    setConfig({ ...(config as CollectionsDialerConfig), [key]: value })
    setChanged(true)
  }

  const onSave = async () => {
    const temp = cloneDeep(inputs)
    let hasError = false
    Object.keys(temp).forEach((key) => {
      temp[key].error = InputValidate(temp[key])
      if (temp[key].error) hasError = true
    })
    setInputs(temp)
    if (hasError) return

    const data: Record<string, any> = {
      enabled: !!config?.enabled,
      recordCalls: !!config?.recordCalls,
      autoResolveCured: config?.autoResolveCured !== false,
      callerId: String(temp.callerId.value || ''),
      recordingAnnouncement: String(temp.recordingAnnouncement.value || ''),
    }
    ;[
      'agentRingTimeout',
      'contactRingTimeout',
      'queueDaysPastDue',
      'queueAmountWeight',
      'queueFirstPaymentPoints',
      'queueSecondPaymentPoints',
    ].forEach(
      (key) => (data[key] = Number(temp[key].value)),
    )
    data.exceptionTags = toTags(temp.exceptionTags.value)

    setLoading(true)
    try {
      const saved = await saveCollectionsDialerConfig(data)
      setConfig(saved)
      setChanged(false)
      toast('Calling settings have been saved.', { type: 'info' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative pt-4">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSave} />

      <div className="max-w-3xl flex flex-col gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-1">Calling</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Collectors place calls from the loan and from Servicing &gt; Collections. The platform rings their own phone
            first and connects them to the borrower when they pick up, so the company number is the caller ID throughout
            and nobody's personal mobile is disclosed.
          </p>
        </div>

        <div className="border rounded p-4 flex flex-col gap-3">
          <RenderInput
            input={{
              inputType: 'toggle',
              title: 'Allow calling from the app',
              tooltip: 'While this is off, the Call buttons explain that calling is not set up rather than failing.',
              value: !!config?.enabled,
            }}
            Key="enabled"
            onChange={(_key: string, value: any) => setFlag('enabled', !!value)}
          />
          <RenderInput
            input={{
              inputType: 'toggle',
              title: 'Record calls',
              tooltip:
                'Both parties hear the notice below before the borrower is dialed. Check what the states you lend in require before turning this on.',
              value: !!config?.recordCalls,
            }}
            Key="recordCalls"
            onChange={(_key: string, value: any) => setFlag('recordCalls', !!value)}
          />
          <RenderInput
            input={{
              inputType: 'toggle',
              title: 'Clear a loan off the queue once it is no longer past due',
              tooltip:
                'Only applies to loans the nightly pass added. One somebody put on the list by hand, or snoozed, is left alone.',
              value: config?.autoResolveCured !== false,
            }}
            Key="autoResolveCured"
            onChange={(_key: string, value: any) => setFlag('autoResolveCured', !!value)}
          />
        </div>

        {groups.map((group) => (
          <div key={group.title}>
            <h4 className="font-semibold text-[15px]">{group.title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{group.description}</p>
            <div className="grid md:grid-cols-3 gap-4">
              {group.keys.map((key) => (
                <div key={key} className={inputs[key].span === 'full' ? 'md:col-span-3' : ''}>
                  <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
